import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { requestMagicLink } from '../lib/auth';
import { sharedStyles, T } from '../components/Shared';

function GoogleLogo({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function Divider({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '14px 0' }}>
      <div style={{ flex: 1, height: 1, background: '#dadce0' }} />
      <div style={{ fontSize: 11, color: '#9aa0a6', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ flex: 1, height: 1, background: '#dadce0' }} />
    </div>
  );
}

const ERROR_LABELS: Record<string, string> = {
  missing_token:           'The sign-in link was incomplete. Please request a new one.',
  expired:                 'That sign-in link expired or was already used. Please request a new one.',
  no_user:                 'That email isn\'t registered. Contact your YEA admin to be added.',
  oauth_invalid:           'Google sign-in could not be verified. Please try again.',
  oauth_denied:            'Google sign-in was cancelled.',
  oauth_token_failed:      'Google sign-in failed at the token step. Please try again.',
  oauth_userinfo_failed:   'Could not fetch your Google profile. Please try again.',
  oauth_no_verified_email: 'Your Google email is not verified.',
  oauth_unconfigured:      'Google sign-in is not configured yet. Use email below.',
};

export function Login() {
  const [params] = useSearchParams();
  const errorCode = params.get('error');
  // Deep-link target captured by RequireAuth/RequirePage (e.g. /admin/intake).
  const next = params.get('next');
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<'form' | 'sent' | 'error'>('form');
  const [submitting, setSubmitting] = useState(false);

  // Localhost-only quick sign-in. Mirrors the server gate in
  // functions/api/auth/dev-login.ts — this block never renders in production
  // because the production host is never a loopback name.
  const host = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const isLocalhost =
    ['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]'].includes(host) ||
    host.endsWith('.localhost') || host.endsWith('.local');
  const [devEmail, setDevEmail] = useState('');
  const [devBusy, setDevBusy] = useState(false);

  const devLogin = async () => {
    setDevBusy(true);
    try {
      const res = await fetch('/api/auth/dev-login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: devEmail.trim() || undefined, next }),
      });
      if (!res.ok) { setPhase('error'); return; }
      const data = await res.json() as { next?: string };
      // Hard nav so the auth context re-fetches /api/auth/me cleanly.
      window.location.href = data.next || '/admin';
    } catch {
      setPhase('error');
    } finally {
      setDevBusy(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const ok = await requestMagicLink(email.trim(), next);
      setPhase(ok ? 'sent' : 'error');
    } catch {
      setPhase('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#f8f9fa',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{
        width: 420, maxWidth: '100%',
        background: '#fff', borderRadius: 16, padding: 32,
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        border: `1px solid ${T.border}`,
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
          <div style={{
            background: '#fff', borderRadius: 8, border: `1px solid ${T.border}`, padding: '6px 9px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <img src="/assets/gbi-logo.png" alt="GBI" style={{ height: 38, width: 'auto', display: 'block' }} />
          </div>
          <div>
            <div style={{
              fontFamily: sharedStyles.fontDisplay, fontWeight: 700, fontSize: 14,
              background: T.brandGradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              whiteSpace: 'nowrap',
            }}>AI Intelligence</div>
            <div style={{ fontSize: 10, color: T.fgMuted, letterSpacing: '0.04em', marginTop: 1 }}>by YEA</div>
          </div>
        </div>

        {phase === 'sent' ? (
          <>
            <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 22, fontWeight: 700, color: T.fgPrimary, marginBottom: 8 }}>
              Check your email
            </div>
            <div style={{ fontSize: 14, color: T.fgSecondary, lineHeight: 1.5 }}>
              If <strong>{email}</strong> is registered, we've sent a sign-in link.
              The link expires in 15 minutes.
            </div>
            <button
              onClick={() => { setPhase('form'); setEmail(''); }}
              style={{
                marginTop: 20, background: 'transparent', border: `1px solid ${T.border}`,
                borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: T.fgSecondary,
              }}
            >
              Use a different email
            </button>
          </>
        ) : (
          <>
            <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 22, fontWeight: 700, color: T.fgPrimary, marginBottom: 6 }}>
              Sign in
            </div>
            <div style={{ fontSize: 13, color: T.fgSecondary, marginBottom: 20 }}>
              We'll email you a one-time sign-in link.
            </div>

            {errorCode && ERROR_LABELS[errorCode] && (
              <div style={{
                background: 'rgba(232,80,26,0.08)', border: `1px solid ${T.magenta}`,
                borderRadius: 8, padding: '10px 12px', marginBottom: 16,
                fontSize: 12, color: T.magenta,
              }}>
                {ERROR_LABELS[errorCode]}
              </div>
            )}

            {/* Google Sign-In — fastest path if user has a Google account. */}
            <a href="/api/auth/google/start" style={{ display: 'block', textDecoration: 'none', marginBottom: 14 }}>
              <button type="button" style={{
                width: '100%', padding: '12px 14px', display: 'flex',
                alignItems: 'center', justifyContent: 'center', gap: 10,
                background: '#fff', border: `1px solid ${T.border}`, borderRadius: 8,
                cursor: 'pointer', fontFamily: sharedStyles.fontBody, fontSize: 14,
                fontWeight: 500, color: T.fgPrimary,
              }}>
                <GoogleLogo size={18} />
                Sign in with Google
              </button>
            </a>

            <Divider label="or with email" />

            <form onSubmit={submit}>
              <input
                type="email"
                placeholder="you@yeabusiness.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={{
                  width: '100%', padding: '12px 14px',
                  background: '#f8f9fa', border: `1px solid ${T.border}`, borderRadius: 8,
                  fontFamily: sharedStyles.fontBody, fontSize: 14, color: T.fgPrimary,
                  outline: 'none', marginBottom: 14,
                }}
              />
              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: '100%', padding: '12px 14px',
                  background: 'linear-gradient(90deg,#12B5C4,#1E7FD4)', color: '#fff',
                  border: 'none', borderRadius: 8, cursor: submitting ? 'wait' : 'pointer',
                  fontWeight: 600, fontSize: 14,
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                {submitting ? 'Sending…' : 'Send sign-in link'}
              </button>
            </form>

            <div style={{ marginTop: 20, fontSize: 11, color: T.fgMuted, textAlign: 'center' }}>
              Need access? Ask a YEA admin to invite you.
            </div>

            {isLocalhost && (
              <div style={{
                marginTop: 22, padding: 14, borderRadius: 10,
                background: '#f8f9fa', border: `1px dashed ${T.borderBright}`,
              }}>
                <div style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: T.fgSecondary, marginBottom: 8,
                }}>
                  Localhost dev sign-in
                </div>
                <input
                  type="email"
                  placeholder="blank = first admin (yxchu@yeabusiness.com)"
                  value={devEmail}
                  onChange={e => setDevEmail(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', boxSizing: 'border-box',
                    background: '#fff', border: `1px solid ${T.border}`, borderRadius: 8,
                    fontFamily: sharedStyles.fontBody, fontSize: 13, color: T.fgPrimary,
                    outline: 'none', marginBottom: 10,
                  }}
                />
                <button
                  type="button"
                  onClick={devLogin}
                  disabled={devBusy}
                  style={{
                    width: '100%', padding: '11px 14px',
                    background: '#202124', color: '#fff',
                    border: 'none', borderRadius: 8, cursor: devBusy ? 'wait' : 'pointer',
                    fontWeight: 600, fontSize: 13, fontFamily: sharedStyles.fontBody,
                    opacity: devBusy ? 0.6 : 1,
                  }}
                >
                  {devBusy ? 'Signing in…' : '⚡ Sign in (localhost only)'}
                </button>
                <div style={{ marginTop: 8, fontSize: 10, color: T.fgMuted, lineHeight: 1.5 }}>
                  Bypasses the email link for local testing. Disabled in production —
                  the server only honours this on loopback hosts.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
